import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdSlot } from '../ads/AdSlot';
import { FullscreenButton } from '../components/FullscreenButton';
import { GameActions } from '../components/LikeButton';
import { NextUp } from '../components/NextUp';
import { sound } from '../lib/audio';
import { readJson, writeJson } from '../lib/storage';
import type { CatalogEntry } from '../games/catalog';
import type { SoloGameDef } from './types';
import { setGameMeta } from '../lib/seo';

/** Single-player wrapper: title, best score, restart and rules. The game owns everything inside the stage. */
export function SoloShell({ entry, def }: { entry: CatalogEntry; def: SoloGameDef }) {
  const bestKey = `tpubg:best:${entry.id}`;
  const saveKey = `tpubg:save:${entry.id}`;
  const [best, setBest] = useState<number | null>(() => readJson<number | null>(bestKey, null));
  const [runNo, setRunNo] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  // Fullscreen keeps the game's own shape: remember its aspect ratio so CSS can fit it to the screen.
  const measure = useCallback(() => {
    const stage = stageRef.current;
    const game = stage?.firstElementChild as HTMLElement | null;
    if (stage && game?.offsetHeight) stage.style.setProperty('--fs-aspect', String(game.offsetWidth / game.offsetHeight));
  }, []);

  useEffect(() => {
    setGameMeta(entry);
  }, [entry.name]);

  // A finished round (the game reported a score): the game bar offers other games to try.
  const [roundDone, setRoundDone] = useState(false);
  const onScore = useCallback(
    (score: number) => {
      if (!Number.isFinite(score)) return;
      setRoundDone(true);
      setBest((b) => {
        const better = b === null || (def.lowerIsBetter ? score < b : score > b);
        if (!better) return b;
        writeJson(bestKey, score);
        return score;
      });
    },
    [bestKey, def.lowerIsBetter],
  );
  const play = useCallback((n: Parameters<typeof sound.play>[0]) => sound.play(n), []);
  const load = useCallback(<T,>(fallback: T): T => readJson<T>(saveKey, fallback), [saveKey]);
  const save = useCallback((v: unknown) => writeJson(saveKey, v), [saveKey]);
  const fmt = def.formatScore ?? ((n: number) => n.toLocaleString());
  const Game = def.Component;

  return (
    <main className="page turn-page solo-page game-page" id="main">
      <div className="turn-head">
        <Link to="/" className="btn btn--ghost btn--small turn-head__back">
          ← All games
        </Link>
        <span className="game-icon" aria-hidden="true">
          <entry.Icon />
        </span>
        <div className="turn-head__title">
          <h1>{entry.name}</h1>
          <p className="muted small">
            {entry.categoryLabel} · Singleplayer
            {best !== null && (
              <>
                {' '}
                · Best {def.scoreLabel ? `${def.scoreLabel.toLowerCase()} ` : ''}
                <strong>{fmt(best)}</strong>
              </>
            )}
          </p>
        </div>
      </div>

      <div className="solo-stage" ref={stageRef} onPointerDown={() => sound.unlock()} onKeyDown={() => sound.unlock()}>
        <Game key={runNo} play={play} onScore={onScore} best={best} load={load} save={save} />
        <div className="game-bar">
          {roundDone ? <NextUp entry={entry} /> : <span className="game-bar__name">{entry.name}</span>}
          <GameActions gameId={entry.id} path={entry.path} />
          <FullscreenButton target={stageRef} beforeEnter={measure} compact />
        </div>
      </div>

      <div className="turn-foot">
        <button type="button" className="btn btn--ghost btn--small" onClick={() => setRunNo((n) => n + 1)}>
          Restart
        </button>
        <details className="turn-rules">
          <summary>How to play</summary>
          <ul className="rules">
            {def.rules.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </details>
      </div>
      <AdSlot placement="board" />
    </main>
  );
}
